import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface PdfColumn<T = Record<string, unknown>> {
  key: keyof T;
  label: string;
  format?: (value: unknown) => string | number;
}

export interface PdfExportOptions<T = Record<string, unknown>> {
  data: T[];
  columns: PdfColumn<T>[];
  fileName?: string;
  title?: string;
}

export interface GridPdfExportOptions {
  rowData: Record<string, unknown>[];
  columnDefs: Array<{ field: string; headerName?: string }>;
  fileName?: string;
  title?: string;
}

// ─────────────────────────────────────────────
// Constants: A4 page dimensions in mm
// usable width = page width - left margin - right margin (14mm each)
// ─────────────────────────────────────────────

const PAGE = {
  PORTRAIT_WIDTH: 210,
  PORTRAIT_HEIGHT: 297,
  LANDSCAPE_WIDTH: 297,
  LANDSCAPE_HEIGHT: 210,
  MARGIN: 14,
} as const;

const PORTRAIT_USABLE = PAGE.PORTRAIT_WIDTH - PAGE.MARGIN * 2; // 182mm
const LANDSCAPE_USABLE = PAGE.LANDSCAPE_WIDTH - PAGE.MARGIN * 2; // 269mm

// mm per character at font-size 9 helvetica — calibrated to autoTable rendering
const MM_PER_CHAR = 1.85;
const CELL_PADDING = 6; // mm padding per cell (left + right)
const MIN_COL_WIDTH = 14; // mm minimum column width

// ─────────────────────────────────────────────
// Auto-detect: infer formatter from key name
// (mirrors excel.ts and csv.ts — same keywords)
// ─────────────────────────────────────────────

const DATE_KEYWORDS = [
  'created',
  'updated',
  'date',
  'dob',
  'birthday',
  'expiry',
  'expires',
  'modified',
  'timestamp',
];

const CURRENCY_KEYWORDS = [
  'amt',
  'amount',
  'price',
  'cost',
  'fee',
  'charge',
  'revenue',
  'salary',
];

function inferFormatter(
  key: string
): ((value: unknown) => string | number) | null {
  const lower = key.toLowerCase();
  if (DATE_KEYWORDS.some((kw) => lower.includes(kw))) return formatDate;
  if (CURRENCY_KEYWORDS.some((kw) => lower.includes(kw))) return formatCurrency;
  return null;
}

// ─────────────────────────────────────────────
// Core: calculate natural content width per column (in mm)
// Based on max of header length vs longest cell value
// ─────────────────────────────────────────────

function calcNaturalWidths<T = Record<string, unknown>>(
  data: T[],
  columns: PdfColumn<T>[]
): number[] {
  return columns.map((col) => {
    const formatter = col.format ?? inferFormatter(col.key as string);
    const headerLen = col.label.length;

    const maxDataLen = data.reduce((max, row) => {
      const raw = (row as Record<string, unknown>)[col.key as string];
      const value = formatter ? formatter(raw) : (raw ?? '');
      return Math.max(max, String(value).length);
    }, 0);

    const naturalWidth =
      Math.max(headerLen, maxDataLen) * MM_PER_CHAR + CELL_PADDING;
    return Math.max(naturalWidth, MIN_COL_WIDTH);
  });
}

// ─────────────────────────────────────────────
// Core: resolve orientation BEFORE any scaling
// Uses the raw natural total width to decide
// ─────────────────────────────────────────────

function resolveOrientation(
  naturalTotalWidth: number
): 'portrait' | 'landscape' {
  return naturalTotalWidth <= PORTRAIT_USABLE ? 'portrait' : 'landscape';
}

// ─────────────────────────────────────────────
// Core: scale column widths to fit usable page width
// Only scales down — never scales up
// ─────────────────────────────────────────────

function scaleWidths(naturalWidths: number[], usableWidth: number): number[] {
  const total = naturalWidths.reduce((sum, w) => sum + w, 0);
  if (total <= usableWidth) return naturalWidths; // fits — no scaling needed
  const ratio = usableWidth / total;
  return naturalWidths.map((w) => Math.max(w * ratio, MIN_COL_WIDTH / 2));
}

// ─────────────────────────────────────────────
// Core: map data rows to string arrays for autoTable
// ─────────────────────────────────────────────

function buildTableRows<T = Record<string, unknown>>(
  data: T[],
  columns: PdfColumn<T>[]
): string[][] {
  return data.map((row) =>
    columns.map((col) => {
      const raw = (row as Record<string, unknown>)[col.key as string];
      const formatter = col.format ?? inferFormatter(col.key as string);
      const value = formatter ? formatter(raw) : (raw ?? '');
      return String(value);
    })
  );
}

// ─────────────────────────────────────────────
// Core: build and download the PDF
// ─────────────────────────────────────────────

function buildPdf<T = Record<string, unknown>>(
  data: T[],
  columns: PdfColumn<T>[],
  fileName: string,
  title?: string
): void {
  // Step 1 — calculate natural widths from content (no constraints)
  const naturalWidths = calcNaturalWidths(data, columns);
  const naturalTotal = naturalWidths.reduce((sum, w) => sum + w, 0);

  // Step 2 — resolve orientation from natural total width
  const orientation = resolveOrientation(naturalTotal);
  const usableWidth =
    orientation === 'portrait' ? PORTRAIT_USABLE : LANDSCAPE_USABLE;
  const pageHeight =
    orientation === 'portrait' ? PAGE.PORTRAIT_HEIGHT : PAGE.LANDSCAPE_HEIGHT;
  const pageWidth =
    orientation === 'portrait' ? PAGE.PORTRAIT_WIDTH : PAGE.LANDSCAPE_WIDTH;

  // Step 3 — scale down only if content still overflows in resolved orientation
  const colWidths = scaleWidths(naturalWidths, usableWidth);

  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

  let startY = PAGE.MARGIN;

  // Optional title
  if (title) {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(title, PAGE.MARGIN, startY);
    startY += 8;
  }

  // Timestamp line
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(
    `Exported on ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString()}`,
    PAGE.MARGIN,
    startY
  );
  doc.setTextColor(0);
  startY += 6;

  autoTable(doc, {
    head: [columns.map((col) => col.label)],
    body: buildTableRows(data, columns),
    startY,
    margin: { left: PAGE.MARGIN, right: PAGE.MARGIN },
    tableWidth: 'auto', // let autoTable use our explicit columnStyles widths

    // Explicit per-column widths derived from content analysis
    columnStyles: Object.fromEntries(
      colWidths.map((w, i) => [i, { cellWidth: w }])
    ),

    styles: {
      fontSize: 9,
      cellPadding: 3,
      overflow: 'linebreak',
      font: 'helvetica',
    },

    headStyles: {
      fillColor: [41, 98, 196],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'left',
    },

    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },

    // Page number footer on every page
    didDrawPage: (hookData) => {
      const pageCount = (
        doc as unknown as { internal: { getNumberOfPages: () => number } }
      ).internal.getNumberOfPages();

      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${hookData.pageNumber} of ${pageCount}`,
        pageWidth - PAGE.MARGIN,
        pageHeight - 8,
        { align: 'right' }
      );
      doc.setTextColor(0);
    },
  });

  doc.save(`${fileName}.pdf`);
}

// ─────────────────────────────────────────────
// Export: static columns or dynamic grid
// ─────────────────────────────────────────────

/**
 * Export a flat data array with a defined column map to PDF.
 * Auto-detects date/currency formatting from key names.
 * Auto-switches portrait ↔ landscape based on total content width.
 *
 * @example
 * exportToPdf({
 *   data: orders,
 *   columns: [
 *     { key: 'orderId',   label: 'Order ID'  },
 *     { key: 'createdAt', label: 'Created'   }, // auto date
 *     { key: 'totalAmt',  label: 'Total'     }, // auto currency
 *   ],
 *   fileName: 'orders-report',
 *   title:    'Orders Report',
 * });
 */
export function exportToPdf<T = Record<string, unknown>>({
  data,
  columns,
  fileName = 'export',
  title,
}: PdfExportOptions<T>): void {
  if (!data.length) {
    console.warn('[exportToPdf] No data to export.');
    return;
  }

  buildPdf(data, columns, fileName, title);
}

// ─────────────────────────────────────────────
// Export: raw dynamic grid (columnDefs + rowData)
// for AG Grid / TanStack or similar
// ─────────────────────────────────────────────

/**
 * Export directly from a grid's rowData + columnDefs to PDF.
 * Preserves column order and uses headerName as the PDF header.
 *
 * @example
 * exportGridToPdf({
 *   rowData:    gridRef.current.api.getRenderedNodes().map(n => n.data),
 *   columnDefs: gridRef.current.api.getColumnDefs(),
 *   fileName:   'grid-export',
 *   title:      'Grid Report',
 * });
 */
export function exportGridToPdf({
  rowData,
  columnDefs,
  fileName = 'grid-export',
  title,
}: GridPdfExportOptions): void {
  if (!rowData.length) {
    console.warn('[exportGridToPdf] No rows to export.');
    return;
  }

  const columns: PdfColumn[] = columnDefs.map((col) => ({
    key: col.field,
    label: col.headerName ?? col.field,
  }));

  buildPdf(rowData, columns, fileName, title);
}

// ─────────────────────────────────────────────
// Utility: format helpers for cell values
// (same as excel.ts and csv.ts)
// ─────────────────────────────────────────────

/** Format a JS Date or ISO string as DD/MM/YYYY */
export function formatDate(value: unknown): string {
  if (!value) return '';
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB');
}

/** Format a number as currency string e.g. "₹1,234.56" */
export function formatCurrency(value: unknown, symbol = '₹'): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${symbol}${num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Format a decimal as percentage string e.g. 0.25 → "25.00%" */
export function formatPercent(value: unknown, decimals = 2): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${(num * 100).toFixed(decimals)}%`;
}
